<template>
  <a-modal v-model:visible="dialogShow"
           :title="$t('AccountInfo.InvoiceAdd.addTitle')"
           @cancel="invoiceFormCancel">
    <a-form ref="formRef"
            :model="invoiceFormState"
            :rules="rulesState"
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            name="invoiceAddForm">
      <a-form-item :label="$t('AccountInfo.InvoiceAdd.titleType')"
                   name="title_type">
        <a-radio-group v-model:value="invoiceFormState.title_type"
                       :options="invoiceForm_invoiceTitle_RadioGroup"></a-radio-group>
      </a-form-item>
      <a-form-item :label="$t('AccountInfo.InvoiceAdd.invoiceTitle')"
                   name="title">
        <a-input v-model:value="invoiceFormState.title"></a-input>
      </a-form-item>
      <a-form-item v-if="invoiceFormState.title_type != 0"
                   :label="$t('AccountInfo.InvoiceAdd.defaultInvoiceType')"
                   name="invoice_type">
        <a-radio-group v-model:value="invoiceFormState.invoice_type"
                       :options="invoiceForm_defaultInvoiceType_RadioGroup"></a-radio-group>
      </a-form-item>
      <a-form-item v-if="writeState"
                   :label="$t('AccountInfo.InvoiceAdd.companyCode')"
                   name="company_code">
        <a-input v-model:value="invoiceFormState.company_code"></a-input>
      </a-form-item>
      <a-form-item v-if="writeState"
                   :label="$t('AccountInfo.InvoiceAdd.bankName')"
                   name="bank">
        <a-input v-model:value="invoiceFormState.bank"></a-input>
      </a-form-item>
      <a-form-item v-if="writeState"
                   :label="$t('AccountInfo.InvoiceAdd.bankAccount')"
                   name="bank_account">
        <a-input v-model:value="invoiceFormState.bank_account"></a-input>
      </a-form-item>
      <a-form-item v-if="writeState"
                   :label="$t('AccountInfo.InvoiceAdd.companyAddress')"
                   name="company_address">
        <a-input v-model:value="invoiceFormState.company_address"></a-input>
      </a-form-item>
      <a-form-item v-if="writeState"
                   :label="$t('AccountInfo.InvoiceAdd.companyTelephone')"
                   name="company_telephone">
        <a-input v-model:value="invoiceFormState.company_telephone"></a-input>
      </a-form-item>
    </a-form>
    <template #footer>
      <a-button key="back"
                @click="invoiceFormCancel">{{ $t('AccountInfo.InvoiceAdd.cancel') }}</a-button>
      <a-button key="submit"
                type="primary"
                @click="invoiceFormSubmit">{{ $t('AccountInfo.InvoiceAdd.create') }}</a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { InvoiceModel } from '../api/interface/InvoiceInterface'
import { invoiceAdd } from '../api/Invoice'

const formRef = ref()
const { t } = useI18n()
const props = defineProps<{ state: boolean }>()
const emits = defineEmits(['dialogVisibleUpdate', 'refreshInvoice'])
const invoiceFormState = reactive<InvoiceModel>({
  title: '',
  title_type: 0,
  invoice_type: 0,
  bank: '',
  bank_account: '',
  company_code: '',
  company_address: '',
  company_telephone: ''
})
const dialogShow = computed({
  get: () => props.state,
  set: () => ''
})

// declared static
const writeState = computed(() => (invoiceFormState.title_type !== 0))
const rulesState = computed(() => (invoiceFormState.title_type === 1 ? (invoiceFormState.invoice_type === 1 ? invoiceForm_InvoiceType_FieldRuler : invoiceForm_InvoiceType_FieldRulerSecond) : invoiceForm_InvoiceType_normalRuler))

const invoiceForm_invoiceTitle_RadioGroup = computed(() => [
  { label: t('AccountInfo.InvoiceAdd.personal'), value: 0 },
  { label: t('AccountInfo.InvoiceAdd.company'), value: 1 },
  { label: t('AccountInfo.InvoiceAdd.institution'), value: 2 },
])
const invoiceForm_defaultInvoiceType_RadioGroup = computed(() => [
  { label: t('AccountInfo.InvoiceAdd.vatGeneralInvoice'), value: 0 },
  { label: t('AccountInfo.InvoiceAdd.vatSpecialInvoice'), value: 1 },
])

const invoiceForm_InvoiceType_normalRuler = computed(() => ({
  title_type: [{ type: 'number', required: true }],
  title: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.titleRequired'), trigger: 'blur' }],
  invoice_type: [{ type: 'number', required: true }]
}))

const invoiceForm_InvoiceType_FieldRuler = computed(() => ({
  title_type: [{ type: 'number', required: true }],
  title: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.titleRequired'), trigger: 'blur' }],
  invoice_type: [{ type: 'number', required: true }],
  company_code: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.companyCodeRequired'), trigger: 'blur' }],
  bank: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.bankNameRequired'), trigger: 'blur' }],
  bank_account: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.bankAccountRequired'), trigger: 'blur' }],
  company_address: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.companyAddressRequired'), trigger: 'blur' }],
  company_telephone: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.companyTelephoneRequired'), trigger: 'blur' }],
}))

const invoiceForm_InvoiceType_FieldRulerSecond = computed(() => ({
  title_type: [{ type: 'number', required: true }],
  title: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.titleRequired'), trigger: 'blur' }],
  invoice_type: [{ type: 'number', required: true }],
  company_code: [{ type: 'string', required: true, message: t('AccountInfo.InvoiceAdd.companyCodeRequired'), trigger: 'blur' }]
}))

// declared Method
const invoiceFormSubmit = () => {
  formRef.value.validate().then(() => {
    invoiceAdd(invoiceFormState).then(() => {
      message.success(t('AccountInfo.InvoiceAdd.addSuccess'))
      emits('dialogVisibleUpdate')
      emits('refreshInvoice')
    }).catch(() => {
      message.error(t('AccountInfo.InvoiceAdd.addFailed'))
    })
  })
}

const invoiceFormCancel = () => {
  emits('dialogVisibleUpdate')
}

</script>


<style lang="less" scoped></style>
